// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import ChatbotModal from "src/components/containers/shared/ChatbotModal/ChatbotModal";
import OperatingManualSidebar from "src/components/containers/KnowledgeHub/OperatingManualSidebar/OperatingManualSidebar";
import OperatingManualContent from "src/components/containers/KnowledgeHub/OperatingManualContent/OperatingManualContent";
import PublicationInformation from "src/components/containers/KnowledgeHub/PublicationInformation/PublicationInformation";
import ImportantInformation from "src/components/containers/KnowledgeHub/ImportantInformation/ImportantInformation";
import SystemDescription from "src/components/containers/KnowledgeHub/SystemDescription/SystemDescription";
import SoftwareUpdate from "src/components/containers/KnowledgeHub/SoftwareUpdate/SoftwareUpdate";
import Features from "src/components/containers/KnowledgeHub/Features/Features";
import HeadMountedDeviceControls from "src/components/containers/KnowledgeHub/HeadMountedDeviceControls/HeadMountedDeviceControls";
import PortalOverview from "src/components/containers/KnowledgeHub/PortalOverview/PortalOverview";
import InstructionsForUse from "src/components/containers/KnowledgeHub/InstructionsForUse/InstructionsForUse";
import VirtualRealityDevice from "src/components/containers/KnowledgeHub/VirtualRealityDevice/VirtualRealityDevice";
import FAQ from "src/components/containers/KnowledgeHub/FAQ/FAQ";
import Browser from "src/components/containers/KnowledgeHub/Browser/Browser";
import Security from "src/components/containers/KnowledgeHub/Security/Security";
import ContactUs from "src/components/containers/KnowledgeHub/ContactUs/ContactUs";
import AppendixA from "src/components/containers/KnowledgeHub/AppendixA/AppendixA";
import AppendixB from "src/components/containers/KnowledgeHub/AppendixB/AppendixB";
import AppendixC from "src/components/containers/KnowledgeHub/AppendixC/AppendixC";
import AppendixD from "src/components/containers/KnowledgeHub/AppendixD/AppendixD";
import AppendixE from "src/components/containers/KnowledgeHub/AppendixE/AppendixE";
import AppendixF from "src/components/containers/KnowledgeHub/AppendixF/AppendixF";
import OperatingManualMobileNav from "src/components/containers/KnowledgeHub/OperatingManualMobileNav/OperatingManualMobileNav";
// import { SpeechBubbleIcon } from "src/assets/icons";

// Constants
import { FIRST_MANUAL_ROUTE, LAST_MANUAL_ROUTE } from "src/constants/enums";

type OperatingManualType = {
  isTest?: boolean;
};

const OperatingManual = ({ isTest = false }: OperatingManualType) => {
  const { t } = useTranslation("operating-manual");

  const [openChatbotModal, setOpenChatbotModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navbarOptions = useMemo(
    () => [
      {
        route: FIRST_MANUAL_ROUTE,
        label: t("Publication Information"),
        subRoutes: [{ route: "disclaimers", label: t("Disclaimers") }],
      },
      {
        route: "important-information",
        label: t("Important Information - Read Before Use"),
        subRoutes: [
          {
            route: "warnings-and-precautions",
            label: t("Warnings & Precautions"),
          },
        ],
      },
      {
        route: "system-description",
        label: `1. ${t("System Description")}`,
        subRoutes: [
          {
            route: "intended-use",
            label: `1.1 ${t("Intended Use & Indications for Use")}`,
          },
          {
            route: "usage",
            label: `1.2 ${t("Usage")}`,
          },
          {
            route: "results",
            label: `1.3 ${t("Results")}`,
          },
        ],
      },
      {
        route: "software-updates",
        label: `2. ${t("Software Updates & Specifications")}`,
      },
      {
        route: "features",
        label: `3. ${t("Features")}`,
        subRoutes: [
          {
            route: "general",
            label: `3.1 ${t("General")}`,
          },
          {
            route: "visual-field",
            label: `3.2 ${t("Visual Field")}`,
          },
          {
            route: "visual-acuity",
            label: `3.3 ${t("Visual Acuity")}`,
          },
          {
            route: "contrast-sensitivity",
            label: `3.4 ${t("Contrast Sensitivity")}`,
          },
          {
            route: "color-vision",
            label: `3.5 ${t("Color Vision")}`,
          },
        ],
      },
      {
        route: "head-mounted-device-controls",
        label: `4. ${t("Head-Mounted Device Controls")}`,
        subRoutes: [
          {
            route: "headset-buttons",
            label: `${t("Headset Buttons")}`,
          },
          {
            route: "headset-status-indicators",
            label: `${t("Headset Status Indicators")}`,
          },
          {
            route: "controller-buttons",
            label: `${t("Controller Buttons")}`,
          },
          {
            route: "controller-status-indicators",
            label: `${t("Controller Status Indicators")}`,
          },
        ],
      },
      {
        route: "portal-overview",
        label: `5. ${t("Portal Overview")}`,
        subRoutes: [
          {
            route: "patient-page",
            label: `5.1 ${t("Patient Page")}`,
          },
          {
            route: "dashboard-page",
            label: `5.2 ${t("Dashboard Page")}`,
          },
          {
            route: "live-update-page",
            label: `5.3 ${t("Live Update Page")}`,
          },
        ],
      },
      {
        route: "instructions-for-use",
        label: `6. ${t("Instructions for Use")}`,
        subRoutes: [
          {
            route: "account-registration",
            label: `6.1 ${t("Account Registration & Initial Set Up")}`,
          },
          {
            route: "configure-settings",
            label: `6.2 ${t("Configure Clinic Settings")}`,
          },
          {
            route: "turning-on-the-vr-device",
            label: `6.3 ${t("Turning On the VR Device")}`,
          },
          {
            route: "create-select-patient",
            label: `6.4 ${t("Create / Select a Patient")}`,
          },
          {
            route: "create-an-exam",
            label: `6.5 ${t("Create an Exam")}`,
          },
          {
            route: "launching-an-exam",
            label: `6.6 ${t("Launching an Exam")}`,
          },
          {
            route: "viewing-results",
            label: `6.7 ${t("Live Monitor & View Results")}`,
          },
          {
            route: "progression-analysis",
            label: `6.8 ${t("Progression Analysis")}`,
          },
          {
            route: "external-exams",
            label: `6.9 ${t("External Exams")}`,
          },
          {
            route: "tutorial-hub",
            label: `6.10 ${t("Knowledge Hub")}`,
          },
        ],
      },
      {
        route: "virtual-reality-device",
        label: `7. ${t("Virtual Reality (VR) Device")}`,
        subRoutes: [
          {
            route: "set-up-wifi",
            label: `7.1 ${t("Instructions to Set Up Wi-Fi")}`,
          },
          {
            route: "vr-device-care",
            label: `7.2 ${t("VR Device Care")}`,
          },
          {
            route: "device-lifetime-expectancy",
            label: `7.3 ${t("Device Lifetime Expectancy")}`,
          },
        ],
      },
      {
        route: "faq",
        label: `8. ${t("FAQ")}`,
        subRoutes: [
          {
            route: "faq-new-exam",
            label: `8.1 ${t("I want to start a new exam, but the VR device is still linked to a previous exam. How do I fix this?")}`,
          },
          {
            route: "faq-controller-visibility",
            label: `8.2 ${t("Why can't I see the controller?")}`,
          },
          {
            route: "faq-room-off-centre",
            label: `8.3 ${t("Why does the room in the headset now look off-centre?")}`,
          },
          {
            route: "faq-vr-reset",
            label: `8.4 ${t("The VR device is not working and I want to reset it. What should I do?")}`,
          },
          {
            route: "faq-reset-password",
            label: `8.5 ${t("I forgot my password. How do I reset it?")}`,
          },
          {
            route: "faq-edit-exam-settings",
            label: `8.6 ${t("How can I edit the settings of an exam that I've already started?")}`,
          },
          {
            route: "faq-move-exam-patient",
            label: `8.7 ${t("I administered an exam under the wrong patient. How do I move it to the correct patient?")}`,
          },
          {
            route: "faq-deviation-results",
            label: `8.8 ${t("I don't see the total or pattern deviation results for a visual field. Why is that?")}`,
          },
          {
            route: "faq-controller-not-working",
            label: `8.9 ${t("The controller(s) is not working. What should I do?")}`,
          },
          {
            route: "faq-exam-wont-link",
            label: `8.10 ${t("The exam won't link to the headset.")}`,
          },
          {
            route: "faq-turn-on-controller",
            label: `8.11 ${t("How do I turn on the controller?")}`,
          },
          {
            route: "faq-battery-life",
            label: `8.12 ${t("How long should the battery last on the headset?")}`,
          },
          {
            route: "faq-charging-overnight",
            label: `8.13 ${t("Is it ok to leave the headset on and charging overnight?")}`,
          },
          {
            route: "faq-add-user",
            label: `8.14 ${t("How do I add another user to the account?")}`,
          },
          {
            route: "faq-headset-offline",
            label: `8.15 ${t("The headset is saying offline, when it's not, what do I do?")}`,
          },
          {
            route: "faq-connect-wifi",
            label: `8.16 ${t("How do I connect to wifi?")}`,
          },
          {
            route: "faq-wifi-password",
            label: `8.17 ${t("I'm connected to Wi-Fi, but it says the password is wrong or it won't connect. What should I do?")}`,
          },
          {
            route: "faq-no-devices",
            label: `8.18 ${t("It says no devices available. What should I do?")}`,
          },
          {
            route: "faq-prescription",
            label: `8.19 ${t("Do I need to enter prescription into the patient information?")}`,
          },
          {
            route: "faq-glasses",
            label: `8.20 ${t("Should patients wear glasses/correction with the headset?")}`,
          },
          {
            route: "faq-lighting",
            label: `8.21 ${t("What's the best lighting for the headset?")}`,
          },
          {
            route: "faq-headset-fitting",
            label: `8.22 ${t("What's the best way to put on the headset?")}`,
          },
          {
            route: "faq-transfer-devices",
            label: `8.23 ${t("How can I switch devices between clinics I have access to?")}`,
          },
        ],
      },
      {
        route: "browser",
        label: `9. ${t("Browser")}`,
      },
      {
        route: "security",
        label: `10. ${t("Security")}`,
      },
      {
        route: "contact-us",
        label: `11. ${t("Contact Us")}`,
      },
      {
        route: "appendix-a",
        label: t("Appendix A - Symbol Glossary"),
      },
      {
        route: "appendix-b",
        label: t("Appendix B - Visual Field Patient Perspective"),
      },
      {
        route: "appendix-c",
        label: t("Appendix C - Color Vision D-15 Patient Perspective"),
      },
      {
        route: "appendix-d",
        label: t("Appendix D - Visual Acuity Tumbling E Patient Perspective"),
      },
      {
        route: "appendix-e",
        label: t(
          "Appendix E - Contrast Sensitivity Tumbling E Patient Perspective"
        ),
      },
      {
        route: LAST_MANUAL_ROUTE,
        label: t("Appendix F - Portal Icon Glossary"),
      },
    ],
    [t]
  );

  const [selectedNavbarOption, setSelectedNavbarOption] = useState(() => {
    const selectedRouteIndex = navbarOptions.findIndex((option) =>
      location.pathname.includes(option.route)
    );
    if (selectedRouteIndex > -1) {
      return navbarOptions[selectedRouteIndex];
    }

    return navbarOptions[0];
  });

  useEffect(() => {
    // if on the operating-manual page and the user clicks the help button on the navbar,
    // reset the selected section to publication-information
    if (location.pathname === "/knowledge-hub/operating-manual") {
      navigate("/knowledge-hub/operating-manual/publication-information");
      setSelectedNavbarOption(navbarOptions[0]);
    }
  }, [navbarOptions, location.pathname, selectedNavbarOption.route, navigate]);

  const handleSelectNavbarOption = useCallback(
    (option: { route: string; label: string }) => {
      setSelectedNavbarOption(option);

      const manualUrl = `/knowledge-hub/operating-manual/${option.route}`;

      if (location.pathname !== manualUrl) {
        navigate(manualUrl);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [location.pathname, navigate]
  );

  const handleSelectSubRoute = useCallback(
    (
      option: { route: string; label: string },
      subOption: { route: string; label: string }
    ) => {
      setSelectedNavbarOption(option);

      const manualUrl = `/knowledge-hub/operating-manual/${option.route}/${subOption.route}`;

      if (location.pathname !== manualUrl) {
        navigate(manualUrl);
        const element = document.getElementById(subOption.route);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }
    },
    [location.pathname, navigate]
  );

  // wait until the page changes to auto-scroll to a component
  useEffect(() => {
    const subPath = location.pathname.split("/").pop();

    setTimeout(() => {
      if (subPath) {
        const element = document.getElementById(subPath);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }
    }, 200);
  }, [location.pathname]);

  const handleMobileNavigation = useCallback(
    (indexOffset: number) => {
      const selectedRouteIndex = navbarOptions.findIndex((option) =>
        location.pathname.includes(option.route)
      );

      if (selectedRouteIndex > -1) {
        const manualOption = navbarOptions[selectedRouteIndex + indexOffset];
        const manualUrl = `/knowledge-hub/operating-manual/${manualOption.route}`;

        setSelectedNavbarOption(manualOption);
        navigate(manualUrl);
      }
    },
    [location.pathname, navbarOptions, navigate]
  );

  return (
    <Grid container>
      <Grid item xs={0} md={3.5} sx={{ display: { xs: "none", md: "flex" } }}>
        <OperatingManualSidebar
          isTest={isTest}
          navbarOptions={navbarOptions}
          handleSelectNavbarOption={handleSelectNavbarOption}
          handleSelectSubRoute={handleSelectSubRoute}
        />
      </Grid>

      {/* <Box
        role="button"
        sx={{
          position: "fixed",
          right: "20px",
          bottom: "20px",
          padding: "16px",
          borderRadius: "50%",
          backgroundColor: "#E2772E",
          color: "#FFF",
          cursor: "pointer",
        }}
        onClick={() => setOpenChatbotModal(true)}
      >
        <SpeechBubbleIcon />
      </Box> */}
      <ChatbotModal
        open={openChatbotModal}
        onClose={() => setOpenChatbotModal(false)}
      />

      <Grid item xs={12} md={8.5} height="fit-content">
        {selectedNavbarOption.route === "publication-information" ? (
          <PublicationInformation title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "important-information" ? (
          <ImportantInformation title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "system-description" ? (
          <SystemDescription title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "software-updates" ? (
          <SoftwareUpdate title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "features" ? (
          <Features title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "head-mounted-device-controls" ? (
          <HeadMountedDeviceControls title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "portal-overview" ? (
          <PortalOverview title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "instructions-for-use" ? (
          <InstructionsForUse
            title={selectedNavbarOption.label}
            handleSelectNavbarOption={handleSelectNavbarOption}
          />
        ) : selectedNavbarOption.route === "virtual-reality-device" ? (
          <VirtualRealityDevice title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "faq" ? (
          <FAQ title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "browser" ? (
          <Browser title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "security" ? (
          <Security title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "contact-us" ? (
          <ContactUs title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "appendix-a" ? (
          <AppendixA title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "appendix-b" ? (
          <AppendixB title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "appendix-c" ? (
          <AppendixC title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "appendix-d" ? (
          <AppendixD title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "appendix-e" ? (
          <AppendixE title={selectedNavbarOption.label} />
        ) : selectedNavbarOption.route === "appendix-f" ? (
          <AppendixF title={selectedNavbarOption.label} />
        ) : (
          <OperatingManualContent title={selectedNavbarOption.label}>
            <Typography>{t("Page Not Found")}</Typography>
          </OperatingManualContent>
        )}

        <Box sx={{ mt: { xs: "60px", md: "0px" } }}>
          <OperatingManualMobileNav
            navbarOptions={navbarOptions}
            handleSelectNavbarOption={handleSelectNavbarOption}
            handleSelectSubRoute={handleSelectSubRoute}
            handleMobileNavigation={handleMobileNavigation}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default OperatingManual;
